// src/pages/Godown/EditGodown.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import godownService from '../../services/godown.service';
import regionService from '../../services/region.service';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';

const EditGodown = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [regions, setRegions]           = useState([]);
  const [loadingRegions, setLoadingReg] = useState(false);
  const [toast, setToast]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors]             = useState({});

  const [formData, setFormData] = useState({
    name:      '',
    location:  '',
    manager:   '',
    regionIds: [],
  });

  useEffect(() => {
    const init = async () => {
      try {
        setLoadingReg(true);

        // ✅ Load regions globally (across all companies) — same as AddGodown
        const [godownRes, regionsRes] = await Promise.all([
          godownService.getGodownById(id),
          regionService.getAllRegionsGlobal(),
        ]);

        if (godownRes.success) {
          const g = godownRes.data;
          setFormData({
            name:      g.name     || '',
            location:  g.location || '',
            manager:   g.manager  || '',
            regionIds: (g.regions || []).map(r => r.id),
          });
        }

        if (regionsRes.success && regionsRes.data) {
          setRegions(regionsRes.data);
        }
      } catch (err) {
        setToast({ message: 'Failed to load godown data', type: 'error' });
      } finally {
        setLoading(false);
        setLoadingReg(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim())     e.name      = 'Godown name is required';
    if (!formData.location.trim()) e.location  = 'Location is required';
    if (!formData.regionIds || formData.regionIds.length === 0) {
      e.regionIds = 'At least one region is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        name:      formData.name.trim(),
        location:  formData.location.trim(),
        manager:   formData.manager.trim() || undefined,
        regionIds: formData.regionIds.map(Number),
      };

      const res = await godownService.updateGodown(id, payload);
      if (res.success) {
        setToast({ message: 'Godown updated successfully!', type: 'success' });
        setTimeout(() => navigate('/godown/view'), 1200);
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to update godown',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="add-godown-page"><p style={{ padding: 40 }}>Loading...</p></div>;

  // Decorate region option labels with company name (same as AddGodown)
  const regionOptions = regions.map(r => ({
    id:   r.id,
    name: r.company?.name ? `${r.name} — ${r.company.name}` : r.name,
  }));

  return (
    <div className="add-godown-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/godown/view">Godowns</a></li>
          <li className="breadcrumb-item active">Edit Godown</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Edit Godown</h4>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Godown Name <span className="required">*</span></label>
                <input type="text" name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  value={formData.name} onChange={handleChange}
                  placeholder="Enter godown name" />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <MultiSelect
                  label={<>Regions <span className="required">*</span></>}
                  options={regionOptions}
                  selected={formData.regionIds}
                  onChange={(ids) => {
                    setFormData(prev => ({ ...prev, regionIds: ids }));
                    if (errors.regionIds) setErrors(prev => ({ ...prev, regionIds: '' }));
                  }}
                  loading={loadingRegions}
                  placeholder="Select Regions"
                  emptyMsg="No regions available"
                />
                {errors.regionIds && <span className="error-message">{errors.regionIds}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Location <span className="required">*</span></label>
                <input type="text" name="location"
                  className={`form-input ${errors.location ? 'error' : ''}`}
                  value={formData.location} onChange={handleChange}
                  placeholder="Enter location/city" />
                {errors.location && <span className="error-message">{errors.location}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Manager Name</label>
                <input type="text" name="manager" className="form-input"
                  value={formData.manager} onChange={handleChange}
                  placeholder="Enter manager name (optional)" />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/godown/view')} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGodown;